import { useEffect, useRef } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

/**
 * Small dot that follows the cursor with subtle lag. Scales up when hovering
 * over interactive elements (a, button, [role="button"]). Disabled on touch
 * devices and under reduced motion.
 *
 * Pure CSS transform — no React state, no per-frame re-renders.
 */
export function CursorCompanion() {
  const dotRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    if (typeof window === 'undefined') return;
    // Touch devices: skip entirely
    if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return;

    const dot = dotRef.current;
    if (!dot) return;
    dot.style.opacity = '1';

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let dotX = mouseX;
    let dotY = mouseY;
    let scale = 1;
    let targetScale = 1;
    let raf = 0;

    const onMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      const target = e.target as HTMLElement | null;
      const interactive = target?.closest('a, button, [role="button"], input, select, textarea');
      targetScale = interactive ? 2.2 : 1;
    };
    const onLeave = () => {
      targetScale = 1;
    };

    const loop = () => {
      // Lag the dot behind the cursor for a premium feel
      dotX += (mouseX - dotX) * 0.22;
      dotY += (mouseY - dotY) * 0.22;
      scale += (targetScale - scale) * 0.18;
      dot.style.transform = `translate3d(${dotX - 5}px, ${dotY - 5}px, 0) scale(${scale})`;
      raf = requestAnimationFrame(loop);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, [reducedMotion]);

  return <div ref={dotRef} className="cursor-companion" aria-hidden="true" />;
}
